import React, { useMemo, useState } from 'react';
import { CharacterPortrait, Scenario, Scene, SituationSortGameScene } from '../../types';
import {
  insertSceneInChapter,
  moveSceneInChapter,
  removeSceneInChapter,
  updateScene,
} from '../../systems/inline-scene-model';
import { buildInsertItem } from './inline-insert-shared';
import { useInlineClipboard } from './inline-clipboard-context';
import { hasLocalBackground, setSceneBackground } from './inline-background-model';
import { InlineInsertForm, InlineInsertProvider } from './inline-insert-context';
import { InlineSceneList } from './InlineSceneList';
import type { AiBlockContext } from './ai-block-guides';
import type { OpenSceneInEditorHandler } from './inline-elements-model';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';
import { getContentItemSummary, uniqueId } from './utils';
import type { InlineScenarioChange, InlineScenarioChangeOptions } from './use-inline-block-history';
import type { SortCategoryActions } from './sort-category-actions';
import { upsertGlobalCategory } from './situation-sort-categories';

interface InlineAddBarProps {
  editMode: boolean;
  onAdd: (type: string, variant: string | undefined, text: string) => void;
  onInsertGenerated?: (body: Omit<Scene, 'id'>) => void;
  onInsertPasted?: (body: Omit<Scene, 'id'>) => void;
  aiContext?: AiBlockContext;
  onOpenSettings?: () => void;
}

const InlineAddBar: React.FC<InlineAddBarProps> = ({
  editMode,
  onAdd,
  onInsertGenerated,
  onInsertPasted,
  aiContext,
  onOpenSettings,
}) => {
  const [open, setOpen] = useState(false);
  const clipboard = useInlineClipboard();
  if (!editMode) return null;
  return (
    <div className={inline.addBar}>
      {!open ? (
        <div className={inline.addBarCollapsed}>
          <button type="button" className={studio.btnSecondary} onClick={() => setOpen(true)}>
            + Ajouter une ligne
          </button>
        </div>
      ) : (
        <InlineInsertForm
          compact
          onAdd={(type, variant, text) => {
            onAdd(type, variant, text);
            setOpen(false);
          }}
          onInsertGenerated={
            onInsertGenerated
              ? (body) => {
                  onInsertGenerated(body);
                  setOpen(false);
                }
              : undefined
          }
          onInsertPasted={
            onInsertPasted
              ? () => {
                  const body = clipboard?.getPasteBody();
                  if (!body) return;
                  onInsertPasted(body);
                  setOpen(false);
                }
              : undefined
          }
          onCancel={() => setOpen(false)}
          aiContext={aiContext}
          onOpenSettings={onOpenSettings}
        />
      )}
    </div>
  );
};

interface InlineChapterPanelProps {
  chapterId?: string;
  chapterScenes: Scene[];
  scenarioId: string;
  scenario: Scenario;
  editMode: boolean;
  characters: CharacterPortrait[];
  sortCategoryActions?: SortCategoryActions;
  onScenarioChange: InlineScenarioChange;
  onOpenSceneInEditor?: OpenSceneInEditorHandler;
  onPreviewBlock?: (sceneId: string) => void;
  onOpenSettings?: () => void;
}

export const InlineChapterPanel: React.FC<InlineChapterPanelProps> = ({
  chapterId,
  chapterScenes,
  scenarioId,
  scenario,
  editMode,
  characters,
  sortCategoryActions,
  onScenarioChange,
  onOpenSceneInEditor,
  onPreviewBlock,
  onOpenSettings,
}) => {
  const flatBlocks = useMemo(
    () =>
      chapterScenes.map((scene, flatIndex) => ({
        blockId: scene.id,
        item: scene,
        sceneId: scene.id,
        contentIndex: 0,
        flatIndex,
      })),
    [chapterScenes]
  );

  const resolveBackgroundUpward = (afterSceneId: string | null): string | undefined => {
    if (!afterSceneId) return undefined;
    const insertIndex = chapterScenes.findIndex((s) => s.id === afterSceneId);
    if (insertIndex < 0) return undefined;
    for (let i = insertIndex; i >= 0; i--) {
      if (hasLocalBackground(chapterScenes[i]!)) return chapterScenes[i]!.background;
    }
    return undefined;
  };

  const handleInsertAfter = (afterSceneId: string | null, type: string, variant?: string, text?: string) => {
    const item = buildInsertItem(type, variant, text);
    let scene = { ...item, id: uniqueId('scene') } as Scene;
    if (type === 'observation') {
      const inheritedBackground = resolveBackgroundUpward(afterSceneId);
      if (inheritedBackground) scene = setSceneBackground(scene, inheritedBackground);
    }
    onScenarioChange(
      insertSceneInChapter(scenario, chapterId, chapterScenes, afterSceneId, scene),
      { recordBlockHistory: true }
    );
  };

  const handleInsertGenerated = (afterSceneId: string | null, body: Omit<Scene, 'id'>) => {
    const scene = { ...body, id: uniqueId('scene') } as Scene;
    let nextScenario = insertSceneInChapter(
      scenario,
      chapterId,
      chapterScenes,
      afterSceneId,
      scene
    );
    if (body.type === 'situationSortGame') {
      const sortGame = body as SituationSortGameScene;
      for (const category of sortGame.categories) {
        nextScenario = upsertGlobalCategory(nextScenario, category);
      }
    }
    onScenarioChange(nextScenario, { recordBlockHistory: true });
  };

  const handleInsertPasted = (afterSceneId: string | null, body: Omit<Scene, 'id'>) => {
    handleInsertGenerated(afterSceneId, body);
  };

  const aiContext = useMemo((): AiBlockContext => {
    const chapter = chapterId
      ? scenario.metadata.chapters?.find((c) => c.id === chapterId)
      : undefined;
    const recentBlocks = chapterScenes
      .slice(-4)
      .map((s) => `${s.type}: ${getContentItemSummary(s)}`);
    return {
      scenarioTitle: scenario.metadata.title,
      chapterTitle: chapter?.title,
      characters: characters.map((c) => ({ id: c.id, name: c.name })),
      recentBlocks,
      situationSortCategories: scenario.metadata.situationSortCategories,
    };
  }, [chapterId, scenario, chapterScenes, characters]);

  const handleChange = (flatIndex: number, item: Scene, options?: InlineScenarioChangeOptions) => {
    const sceneId = flatBlocks[flatIndex]?.sceneId;
    if (!sceneId) return;
    onScenarioChange(updateScene(scenario, sceneId, { ...item, id: sceneId }), options);
  };

  const handleRemove = (flatIndex: number) => {
    const sceneId = flatBlocks[flatIndex]?.sceneId;
    if (!sceneId) return;
    onScenarioChange(
      removeSceneInChapter(scenario, chapterId, chapterScenes, sceneId),
      { recordBlockHistory: true }
    );
  };

  const handleMove = (flatIndex: number, dir: -1 | 1) => {
    const sceneId = flatBlocks[flatIndex]?.sceneId;
    if (!sceneId) return;
    onScenarioChange(
      moveSceneInChapter(scenario, chapterId, chapterScenes, sceneId, dir),
      { recordBlockHistory: true }
    );
  };

  const lastSceneId = chapterScenes.length > 0 ? chapterScenes[chapterScenes.length - 1]!.id : null;

  return (
    <InlineInsertProvider
      onInsertAfter={handleInsertAfter}
      onInsertGenerated={handleInsertGenerated}
      onInsertPasted={handleInsertPasted}
      aiContext={aiContext}
      scenario={scenario}
      scenarioId={scenarioId}
      onOpenSettings={onOpenSettings}
    >
      <div className={`${inline.chapterScript} ${editMode ? inline.editMode : ''}`}>
        <InlineAddBar
          editMode={editMode}
          onAdd={(type, variant, text) => handleInsertAfter(null, type, variant, text)}
          onInsertGenerated={(body) => handleInsertGenerated(null, body)}
          onInsertPasted={(body) => handleInsertPasted(null, body)}
          aiContext={aiContext}
          onOpenSettings={onOpenSettings}
        />
        <InlineSceneList
          scenes={chapterScenes}
          flatBlocks={flatBlocks}
          scenarioId={scenarioId}
          scenario={scenario}
          editMode={editMode}
          characters={characters}
          sortCategoryActions={sortCategoryActions}
          onChange={handleChange}
          onRemove={handleRemove}
          onMove={handleMove}
          onOpenSceneInEditor={onOpenSceneInEditor}
          onPreviewBlock={onPreviewBlock}
          annotateFirstBlockSceneId
        />
        <InlineAddBar
          editMode={editMode}
          onAdd={(type, variant, text) => handleInsertAfter(lastSceneId, type, variant, text)}
          onInsertGenerated={(body) => handleInsertGenerated(lastSceneId, body)}
          onInsertPasted={(body) => handleInsertPasted(lastSceneId, body)}
          aiContext={aiContext}
          onOpenSettings={onOpenSettings}
        />
      </div>
    </InlineInsertProvider>
  );
};
